import { useEffect, useState } from "react";
import { Settings2, Star, TrendingDown, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";
import AdminReferralSettings from "@/pages/admin/AdminReferralSettings";

const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

export default function AdminLoyalty() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({ points_per_euro: 1, point_value_euros: "0,10" });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    api.get("/admin/loyalty").then(({ data }) => setData(data)).catch(() => {});
    api.get("/admin/loyalty/settings").then(({ data }) => {
      setSettings(data);
      setSettingsForm({
        points_per_euro: data.points_per_euro,
        point_value_euros: (data.point_value_cents / 100).toFixed(2).replace(".", ","),
      });
    }).catch(() => {});
  }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    const cents = Math.round(parseFloat(String(settingsForm.point_value_euros).replace(",", ".")) * 100);
    try {
      const { data } = await api.put("/admin/loyalty/settings", {
        points_per_euro: parseInt(settingsForm.points_per_euro, 10) || 0,
        point_value_cents: Math.min(500, Math.max(1, cents || 10)),
      });
      setSettings(data);
      toast.success("Barème de fidélité mis à jour — appliqué dès la prochaine commande");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  if (!data) {
    return <p data-testid="loyalty-loading" className="py-20 text-center font-serif text-xl text-bark-soft">Chargement…</p>;
  }

  const totalPoints = data.customers.reduce((s, c) => s + (c.loyalty_points || 0), 0);
  const totalUsed = data.movements.reduce((s, m) => s + (m.points_used || 0), 0);
  const totalEarned = data.movements.reduce((s, m) => s + (m.points_earned || 0), 0);

  return (
    <div data-testid="admin-loyalty" className="space-y-8">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-bark">Fidélité</h1>
        <p data-testid="loyalty-subtitle" className="mt-1 text-sm text-bark-soft">
          {settings
            ? `${settings.points_per_euro} point${settings.points_per_euro > 1 ? "s" : ""} gagné${settings.points_per_euro > 1 ? "s" : ""} par euro payé · 1 point = ${formatPrice(settings.point_value_cents)} de remise au panier.`
            : "Chargement du barème…"}
        </p>
      </div>

      <form data-testid="loyalty-settings-form" onSubmit={saveSettings} className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-bark">
          <Settings2 size={18} className="text-honey" /> Barème de fidélité
        </h2>
        <p className="mt-1 text-sm text-bark-soft">Personnalisable à tout moment — le nouveau barème s'applique dès la prochaine commande.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="ls-per-euro" className="mb-1.5 block text-sm font-medium text-bark">Points gagnés par euro payé</label>
            <input
              id="ls-per-euro"
              data-testid="ls-per-euro"
              type="number"
              min="0"
              max="100"
              required
              value={settingsForm.points_per_euro}
              onChange={(e) => setSettingsForm({ ...settingsForm, points_per_euro: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="ls-value" className="mb-1.5 block text-sm font-medium text-bark">Valeur d'un point (€)</label>
            <input
              id="ls-value"
              data-testid="ls-value"
              type="text"
              inputMode="decimal"
              required
              value={settingsForm.point_value_euros}
              onChange={(e) => setSettingsForm({ ...settingsForm, point_value_euros: e.target.value })}
              className={inputClass}
              placeholder="0,10"
            />
            <p className="mt-1 text-xs text-stone-mute">Entre 0,01 € et 5,00 € — au-delà, la valeur est plafonnée à 5,00 €.</p>
          </div>
        </div>
        <button
          data-testid="ls-save"
          type="submit"
          disabled={savingSettings}
          className="mt-4 rounded-full bg-amber-deep px-6 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
        >
          {savingSettings ? "Enregistrement…" : "Enregistrer le barème"}
        </button>
      </form>

      <AdminReferralSettings />

      <div className="grid gap-4 sm:grid-cols-3">
        <div data-testid="loyalty-stat-balance" className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
          <Star size={22} className="text-honey" />
          <p className="mt-3 font-serif text-3xl font-semibold text-bark">{totalPoints}</p>
          <p className="mt-1 text-xs uppercase tracking-[0.15em] text-stone-mute">Points en circulation</p>
        </div>
        <div data-testid="loyalty-stat-earned" className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
          <TrendingUp size={22} className="text-sage" />
          <p className="mt-3 font-serif text-3xl font-semibold text-bark">{totalEarned}</p>
          <p className="mt-1 text-xs uppercase tracking-[0.15em] text-stone-mute">Points distribués</p>
        </div>
        <div data-testid="loyalty-stat-used" className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
          <TrendingDown size={22} className="text-terra" />
          <p className="mt-3 font-serif text-3xl font-semibold text-bark">{totalUsed}</p>
          <p className="mt-1 text-xs uppercase tracking-[0.15em] text-stone-mute">Points dépensés</p>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
        <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-bark">
          <Users size={18} className="text-honey" /> Soldes par client
        </h2>
        {data.customers.length === 0 ? (
          <p className="text-sm text-bark-soft">Aucun client inscrit pour le moment.</p>
        ) : (
          <ul className="divide-y divide-amber-900/10">
            {data.customers.map((c) => (
              <li key={c.id} data-testid={`loyalty-customer-${c.id}`} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <div>
                  <p className="font-medium text-bark">{c.name || "—"}</p>
                  <p className="text-xs text-stone-mute">{c.email}</p>
                </div>
                <span data-testid={`loyalty-balance-${c.id}`} className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-sm font-semibold text-amber-deep">
                  <Star size={13} className="fill-honey text-honey" /> {c.loyalty_points || 0} pts
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
        <h2 className="mb-4 font-serif text-xl font-semibold text-bark">Derniers mouvements</h2>
        {data.movements.length === 0 ? (
          <p data-testid="loyalty-no-movements" className="text-sm text-bark-soft">Aucun point gagné ou dépensé pour le moment.</p>
        ) : (
          <ul className="divide-y divide-amber-900/10">
            {data.movements.map((m) => (
              <li key={m.session_id} data-testid={`loyalty-movement-${m.session_id}`} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <div>
                  <p className="font-medium text-bark">{m.customer_email || "Invité"}</p>
                  <p className="text-xs text-stone-mute">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""} · {formatPrice(Math.round((m.amount || 0) * 100))}
                  </p>
                </div>
                <div className="flex gap-2 text-xs font-semibold">
                  {m.points_earned > 0 && <span className="rounded-full bg-sage-light px-3 py-1 text-sage">+{m.points_earned} gagnés</span>}
                  {m.points_used > 0 && <span className="rounded-full bg-terra/15 px-3 py-1 text-terra">−{m.points_used} utilisés</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
